<template>
  <v-row class="text-left full-height">
    <v-col cols="12">
      <v-card elevation="1" class="my-2 ml-3 mr-2 pa-4 rounded-lg">
        <!-- Phase 1 — #179809: header actions + two-section skeleton -->
        <div class="dashboard-section mb-8">
          <Button
            outlined
            small
            color="brandPrimary"
            icon-left="mdi-eye"
            class="ma-3"
            @click="handlePreviewClick"
          >
            PREVIEW
          </Button>
          <div class="d-flex justify-space-between align-center pa-2">
            <Typography variant="h-3">Page Design</Typography>
            <Button text small color="brandPrimary" @click="handleCustomizePageClick"
              >CUSTOMIZE PAGE</Button
            >
          </div>
          <div class="section-header pa-2">
            <Typography variant="h-4">Top Section</Typography>
          </div>
          <Typography variant="body-2" class="px-2 pt-1 pb-2">
            This is your platform-wide spotlight! Anything added here is visible to all
            participants. Drag and drop to change its position.
          </Typography>
          <div class="pa-2">
            <Button
              outlined
              small
              color="brandPrimary"
              icon-left="mdi-plus"
              @click="handleAddTopSectionComponent"
            >
              ADD COMPONENT
            </Button>
          </div>
          <!-- Phase 2 — top section row list -->
          <div v-if="!loading.dashboard">
            <div v-if="topSectionRows.length === 0" class="empty-state-text py-4 text-center">
              No components added yet
            </div>
            <draggable
              v-else
              :list="topSectionRows"
              :group="`top-section-row`"
              handle=".row-item-handle"
              @change="handleTopSectionReorder"
            >
              <div
                class="row-item d-flex align-center mb-4"
                v-for="row in topSectionRows"
                :key="row.rowId"
              >
                <v-icon class="row-item-handle mr-3">mdi-drag</v-icon>
                <div class="row-title">
                  <Typography as="span" variant="body-2" class="font-weight-bold">{{
                    topSectionRowTitle(row)
                  }}</Typography>
                </div>
                <div class="row-label">
                  <Typography as="span" variant="body-2">{{ topSectionRowLabel(row) }}</Typography>
                </div>
                <div class="row-audience">
                  <v-tooltip bottom :disabled="!audienceLabelTooltip(row)">
                    <template v-slot:activator="{ on, attrs }">
                      <div class="audience-display" v-bind="attrs" v-on="on" title="Audience">
                        <v-icon size="24" class="audience-icon">mdi-account-multiple</v-icon>
                        <span class="audience-text">{{ audienceLabelText(row) }}</span>
                      </div>
                    </template>
                    <span>{{ audienceLabelTooltip(row) }}</span>
                  </v-tooltip>
                </div>
                <div class="row-actions">
                  <v-btn icon small @click.stop="handleEditRow(row)">
                    <v-icon size="20">mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn
                    icon
                    small
                    :loading="loading.deletingRows.includes(row.rowId)"
                    @click.stop="handleDeleteRowConfirm(row.rowId)"
                  >
                    <v-icon size="20">mdi-delete</v-icon>
                  </v-btn>
                </div>
              </div>
            </draggable>
          </div>
        </div>
        <div class="dashboard-section">
          <div class="section-header pa-2">
            <Typography variant="h-4">Programs List</Typography>
          </div>
          <Typography variant="body-2" class="px-2 pt-1 pb-2">
            Organize your participant view by dragging and dropping programs into your preferred
            order. Add subcomponents for exclusive, program-specific content. Note: New programs
            appear at the bottom for easy sorting.
          </Typography>
          <!-- Phase 3 — program cards -->
          <div v-if="loadingProgramRows" class="d-flex justify-center my-4">
            <v-progress-circular indeterminate color="brandPrimary" size="48"></v-progress-circular>
          </div>
          <div v-else-if="programRows.length === 0" class="empty-state-text py-4 text-center">
            There are no programs yet
          </div>
          <template v-else>
            <draggable
              :value="paginatedProgramRows"
              @input="handleProgramPageInput"
              :group="`program-block`"
              handle=".program-card-handle"
              @end="handleProgramCardReorder"
            >
              <div
                v-for="programRow in paginatedProgramRows"
                :key="programRow.rowId"
                class="program-card mb-4 pa-3"
              >
                <div class="d-flex align-center program-card-handle">
                  <v-icon class="drag-handle-icon mr-2">mdi-drag</v-icon>
                  <v-btn icon small @click.stop="toggleProgramExpand(programRow.rowId)">
                    <v-icon small>{{
                      expandedPrograms[programRow.rowId] ? "mdi-chevron-up" : "mdi-chevron-down"
                    }}</v-icon>
                  </v-btn>
                  <Typography as="span" variant="body-2" class="ml-2 font-weight-bold">{{
                    programDisplayName(programRow)
                  }}</Typography>
                  <StatusChip
                    :text="programRow.Program.status === 'Active' ? 'Published' : 'Draft'"
                    :variant="programRow.Program.status === 'Active' ? 'success' : 'warning'"
                    outlined
                    small
                    class="ml-4"
                  />
                  <v-spacer></v-spacer>
                  <v-icon small class="mr-1">mdi-calendar</v-icon>
                  <Typography as="span" variant="body-2" class="mr-4">
                    {{ formatProgramDate(programRow.Program.startDate)
                    }}<template v-if="programRow.Program.endDate">
                      – {{ formatProgramDate(programRow.Program.endDate) }}</template
                    >
                  </Typography>
                  <v-btn icon @click.stop="handleEditProgramSummary(programRow)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </div>
                <!-- Phase 4 — expanded content -->
                <div v-if="expandedPrograms[programRow.rowId]" class="mt-3 pl-2">
                  <!-- ADD SUBCOMPONENT — only visible when no triples exist (empty state) -->
                  <div v-if="programRow.triples.length === 0">
                    <div class="empty-state-text py-2 text-center">No subcomponents added yet</div>
                    <Button
                      text
                      small
                      color="brandPrimary"
                      class="mb-2"
                      @click="handleAddSubcomponent(programRow)"
                    >
                      + ADD SUBCOMPONENT
                    </Button>
                  </div>

                  <!-- Nested triple component cards -->
                  <draggable
                    :list="programRow.triples"
                    :group="`triples-${programRow.rowId}`"
                    handle=".triple-handle"
                    @change="handleTripleReorder(programRow)"
                  >
                    <div
                      v-for="tripleRow in programRow.triples"
                      :key="tripleRow.rowId"
                      class="triple-card d-flex align-center mb-2 pa-2"
                    >
                      <v-icon class="triple-handle mr-2">mdi-drag</v-icon>
                      <div class="row-title">
                        <Typography as="span" variant="body-2" class="font-weight-bold">{{
                          topSectionRowTitle(tripleRow)
                        }}</Typography>
                      </div>
                      <div class="row-label">
                        <Typography as="span" variant="body-2">{{
                          topSectionRowLabel(tripleRow)
                        }}</Typography>
                      </div>
                      <v-spacer></v-spacer>
                      <v-btn icon @click.stop="handleEditRow(tripleRow)">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon @click.stop="handleDeleteRowConfirm(tripleRow.rowId)">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </draggable>

                  <!-- ADD SUBCOMPONENT repeater at bottom when triples exist -->
                  <div v-if="programRow.triples.length > 0">
                    <Button
                      text
                      small
                      color="brandPrimary"
                      class="mb-2"
                      @click="handleAddSubcomponent(programRow)"
                    >
                      + ADD SUBCOMPONENT
                    </Button>
                  </div>
                </div>
              </div>
            </draggable>
            <!-- Pagination controls -->
            <div class="d-flex align-center justify-end pt-4 pagination-controls">
              <span class="pagination-label">Rows per page:</span>
              <v-select
                v-model="programsItemsPerPage"
                :items="programsPerPageOptions"
                dense
                hide-details
                class="pagination-per-page-select"
                @change="programsCurrentPage = 1"
              />
              <span class="pagination-label">
                {{ (programsCurrentPage - 1) * programsItemsPerPage + 1 }}–{{
                  Math.min(programsCurrentPage * programsItemsPerPage, programRows.length)
                }}
                of {{ programRows.length }}
              </span>
              <div class="d-flex align-center">
                <v-btn
                  icon
                  small
                  :disabled="programsCurrentPage === 1"
                  @click="programsCurrentPage--"
                >
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>
                <v-btn
                  icon
                  small
                  :disabled="programsCurrentPage >= totalProgramPages"
                  @click="programsCurrentPage++"
                >
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </div>
      </v-card>
    </v-col>
    <v-navigation-drawer v-model="dialog.dashboardRowType" temporary fixed right width="500">
      <v-toolbar dark color="brand" rounded="0" flat>
        <v-toolbar-title>
          {{ dialog.dashboardRowEditRowId ? "Edit Row" : "Choose an element type" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn
            dark
            text
            @click="
              dialog.dashboardRowType = false;
              dialog.addingToTopSection = false;
            "
          >
            Close
            <v-icon class="ml-2">mdi-close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-progress-circular
        v-if="loading.dashboardRowTypes"
        indeterminate
        color="brandPrimary"
      ></v-progress-circular>
      <div v-else class="pa-4">
        <div
          v-for="rowType in dialog.dashboardRowEditRowId ? [] : rowTypes"
          :key="rowType.rowTypeId"
          class="row-type-container my-4"
          @click="handleAddRowType(rowType)"
        >
          <div>{{ rowType.name }}</div>
        </div>
        <v-card-title v-if="!dialog.addingToTopSection">Program</v-card-title>
        <v-card-text v-if="!dialog.addingToTopSection"
          >Attach an optional program. This row would only appear for those in the program.
          <v-select
            v-model="dialog.dashboardRowProgram"
            :items="programs"
            item-text="displayName"
            item-value="programId"
            label="Program"
            color="brandPrimary"
            outlined
            dense
            clearable
            class="mt-4"
            no-data-text="No programs found"
            :loading="loading.programs"
        /></v-card-text>
        <v-card-actions v-if="dialog.dashboardRowEditRowId" class="d-flex justify-center">
          <v-btn
            color="brandPrimary"
            class="white--text"
            rounded
            depressed
            width="120"
            :loading="loading.editingRow"
            @click="handleUpdateRow"
          >
            {{ loading.editingRow ? "Updating..." : "Update" }}
          </v-btn>
        </v-card-actions>
      </div>
    </v-navigation-drawer>
    <v-navigation-drawer v-model="dialog.componentEditor" temporary fixed right width="500">
      <BillboardCreator
        v-if="
          dialog.componentEditorType === 'billboard' || dialog.componentEditorType === 'headline'
        "
        @close="handleComponentEditorClose"
        @refresh="handleSubcomponentRefresh"
        :row="dialog.componentEditorRow"
        :type="dialog.componentEditorType"
        :rowCount="maxRowPosition"
        :widgetTypes="widgetTypes"
        :showBackButton="dialog.openedFromPicker"
        :clientId="clientId"
        :globalDefaults="globalDefaults"
      />
      <TripleComponentCreator
        v-if="dialog.componentEditorType === 'triple' && dialog.componentEditor"
        @close="handleComponentEditorClose"
        @refresh="handleSubcomponentRefresh"
        @saved="handleTripleComponentSaved"
        :row="dialog.componentEditorRow"
        :widgetTypes="widgetTypes"
        :rowCount="maxRowPosition"
        :programName="dialog.componentEditorProgramName"
        :showBackButton="dialog.openedFromPicker"
        :clientId="clientId"
        :globalDefaults="globalDefaults"
      />
    </v-navigation-drawer>
    <v-navigation-drawer
      :value="dialog.customizePage"
      @input="handleCustomizeDrawerInput"
      temporary
      fixed
      right
      width="500"
    >
      <DashboardCustomizeSideSheet
        v-if="dialog.customizePage"
        ref="customizeSideSheet"
        @close="dialog.customizePage = false"
        @refresh="handleSubcomponentRefresh"
      />
    </v-navigation-drawer>
    <v-navigation-drawer
      :value="dialog.summaryEditor"
      @input="handleSummaryDrawerInput"
      temporary
      fixed
      right
      width="500"
    >
      <SummaryCreator
        v-if="dialog.summaryEditor"
        ref="summaryCreatorSheet"
        :row="dialog.summaryEditorRow"
        @close="dialog.summaryEditor = false"
        @refresh="handleSubcomponentRefresh"
        :globalDefaults="globalDefaults"
      />
    </v-navigation-drawer>
    <v-navigation-drawer v-model="dialog.metricsSummaryEditor" temporary fixed right width="500">
      <MetricsSummaryCreator
        v-if="dialog.metricsSummaryEditor"
        :column="dialog.metricsSummaryEditorColumn"
        :row-id="dialog.metricsSummaryEditorRowId"
        :widget-type-id="dialog.metricsSummaryEditorWidgetTypeId"
        :initial-audience-groups="dialog.metricsSummaryEditorAudienceGroups"
        @close="handleMetricsSummaryEditorClose"
        @saved="handleMetricsSummarySaved"
        :showBackButton="dialog.openedFromPicker"
        :globalDefaults="globalDefaults"
      />
    </v-navigation-drawer>
    <v-navigation-drawer v-model="dialog.componentPicker" temporary fixed right width="500">
      <AddComponentPicker
        v-if="dialog.componentPicker"
        :context="dialog.componentPickerContext || 'topSection'"
        @close="handleComponentPickerClose"
        @select="handleComponentPickerSelect"
      />
    </v-navigation-drawer>
    <v-navigation-drawer
      v-model="dialog.subSidesheetDrawer"
      temporary
      fixed
      right
      width="600"
      class="sub-sidesheet-drawer"
    >
      <ContentCreator
        v-if="dialog.subSidesheetDrawer && dialog.subSidesheetActiveType !== 'leaderboard'"
        :column="dialog.subSidesheetColumn"
        :row="dialog.componentEditorRow"
        :widgetTypes="widgetTypes"
        :rowCount="maxRowPosition"
        :clientId="clientId"
        @close="handleSubSidesheetSaved"
        @saved="handleSubSidesheetSaved"
        @refresh="handleSubSidesheetSaved"
        :globalDefaults="globalDefaults"
      />
      <LeaderboardCreator
        v-else-if="dialog.subSidesheetDrawer && dialog.subSidesheetActiveType === 'leaderboard'"
        :row="dialog.componentEditorRow"
        :type="'leaderboard'"
        :widgetTypes="widgetTypes"
        :rowCount="maxRowPosition"
        @close="handleSubSidesheetSaved"
        @saved="handleSubSidesheetSaved"
        @refresh="handleSubSidesheetSaved"
        @switch-to-content="dialog.subSidesheetActiveType = 'content'"
      />
    </v-navigation-drawer>
    <v-dialog v-model="showPreview" fullscreen persistent transition="dialog-bottom-transition">
      <v-card class="preview-dialog-card">
        <div class="preview-banner">
          <v-icon class="preview-banner__icon">mdi-eye</v-icon>
          <span class="preview-banner__title">Preview Mode</span>
          <v-spacer></v-spacer>
          <span class="preview-banner__label">Previewing as:</span>
          <PreviewAudienceSelector
            v-model="previewSelectedGroups"
            class="preview-banner__selector"
          />
          <v-btn icon class="preview-close-btn" @click="showPreview = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <div class="preview-caption-row" role="alert">
          <div class="preview-caption-inner">
            <v-icon class="preview-caption-icon" size="20">mdi-information-outline</v-icon>
            <span>{{ previewCaptionText }}</span>
          </div>
        </div>
        <v-card-text class="preview-content">
          <div class="global-rows-section">
            <DashboardRowRenderer
              v-for="row in previewTopSectionRows"
              :key="'top-' + rowKey(row)"
              :row="row"
              :previewMode="true"
            />
          </div>
          <!-- Your Programs section - matches PAX layout -->
          <div class="preview-your-programs-section">
            <div class="preview-your-programs-toolbar">
              <div class="preview-toolbar-header">
                <div class="preview-toolbar-icon-box">
                  <v-icon color="white" size="20">mdi-view-dashboard</v-icon>
                </div>
                <span class="preview-toolbar-title">Your Programs</span>
              </div>
              <div class="preview-toolbar-divider"></div>
              <button class="preview-pill-btn" disabled>SORT</button>
              <button class="preview-pill-btn" disabled>FILTER</button>
              <div style="flex: 1"></div>
              <v-text-field
                placeholder="Search Programs...."
                prepend-inner-icon="mdi-magnify"
                hide-details
                dense
                outlined
                disabled
                class="preview-toolbar-search"
              />
            </div>
            <div
              v-for="programRow in previewProgramRows"
              :key="'preview-program-' + rowKey(programRow)"
              class="preview-program-group program-group"
            >
              <ProgramSummaryCard
                :program="buildPreviewProgram(programRow)"
                :preview="true"
                :draft="programRow.Program && programRow.Program.status !== 'Active'"
              />
              <DashboardRowRenderer
                v-for="triple in programRow.triples || []"
                :key="'preview-triple-' + rowKey(triple)"
                :row="{ ...triple, programId: programRow.Program && programRow.Program.programId }"
                :previewMode="true"
              />
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
    <UnsavedChangesDialog
      v-model="dialog.confirmDelete"
      bodyTitle="Are you sure you want to delete this component?"
      bodyText="This action cannot be undone."
      stayLabel="Cancel"
      discardLabel="Yes, delete"
      :loading="loading.deletingRows.includes(dialog.confirmDeleteRowId)"
      @stay="
        dialog.confirmDelete = false;
        dialog.confirmDeleteRowId = null;
      "
      @discard="handleDeleteRowConfirmed"
    />
  </v-row>
</template>

<script>
import ProgramService from "@/services/ProgramService";

// Components
import BillboardCreator from "@/components/account-management/dashboard/BillboardCreator.vue";
import TripleComponentCreator from "@/components/account-management/dashboard/TripleComponentCreator.vue";
import DashboardCustomizeSideSheet from "@/components/account-management/dashboard/DashboardCustomizeSideSheet.vue";
import SummaryCreator from "@/components/account-management/dashboard/SummaryCreator.vue";
import AddComponentPicker from "@/components/account-management/dashboard/AddComponentPicker.vue";
import ContentCreator from "@/components/account-management/dashboard/ContentCreator.vue";
import LeaderboardCreator from "@/components/account-management/dashboard/LeaderboardCreator.vue";
import MetricsSummaryCreator from "@/components/account-management/dashboard/MetricsSummaryCreator.vue";
import DashboardRowRenderer from "@/components/program/participant/dashboard/DashboardRowRenderer.vue";
// Relative path bypasses the jest moduleNameMapper stub so the named export resolves.
import { isRenderableDashboardColumn } from "../program/participant/dashboard/DashboardRowRenderer.vue";
import ProgramSummaryCard from "@/components/participant/programs/ProgramSummaryCard.vue";

import { formRules, userColumns } from "@/shared_data/data";

// Packages / plugins
import draggable from "vuedraggable";
import { mapState } from "vuex";
import StatusChip from "@/components/generic/StatusChip.vue";
import Button from "@/components/generic/Button.vue";
import Typography from "@/components/generic/Typography.vue";
import UnsavedChangesDialog from "@/components/generic/UnsavedChangesDialog.vue";
import PreviewAudienceSelector from "@/components/account-management/dashboard/PreviewAudienceSelector.vue";
import {
  compareNullableNumbers,
  compareSubRows,
  rowKey as rowKeyUtil,
  compareRowIds as compareRowIdsUtil,
} from "@/utils/dashboardOrder";
import {
  isRowVisibleForPreview,
  isProgramVisibleForPreview,
} from "@/services/AudienceEvaluationService";

function initialState() {
  return {
    // Client info
    clientId: null,
    showPreview: false,
    previewSelectedGroups: [],
    previewMockMetrics: {
      activePrograms: 3,
      programsCompletedThisYear: 1,
      pointsEarnedThisMonth: 500,
      pointsEarnedThisYear: 2400,
    },
    rows: [],
    topSectionRows: [],
    programRows: [],
    allProgramRows: [],
    loadingProgramRows: false,
    programsCurrentPage: 1,
    programsItemsPerPage: 10,
    expandedPrograms: {},
    rowTypes: [],
    widgetTypes: [],
    programs: [],

    // Static
    formRules,
    customReportColumns: userColumns,

    // Dialogs, API, and forms
    dialog: {
      dashboardRowType: false,
      dashboardRowEditRowId: null,
      dashboardRowProgram: null,
      componentEditor: false,
      componentEditorType: null,
      componentEditorRow: null,
      componentEditorRowId: null,
      customizePage: false,
      confirmDelete: false,
      confirmDeleteRowId: null,
      addingToTopSection: false,
      subcomponentProgramRow: null,
      subcomponentLearning: false,
      summaryEditor: false,
      summaryEditorRow: null,
      componentPicker: false,
      componentPickerContext: null,
      componentPickerProgramRow: null,
      componentEditorProgramName: null,
      subSidesheetDrawer: false,
      subSidesheetSlot: null,
      subSidesheetColumn: null,
      subSidesheetMode: null,
      subSidesheetActiveType: null,
      metricsSummaryEditor: false,
      metricsSummaryEditorRowId: null,
      metricsSummaryEditorColumn: null,
      metricsSummaryEditorWidgetTypeId: null,
      metricsSummaryEditorAudienceGroups: null,
      openedFromPicker: false,
    },
    form: {
      validReportingInfo: true,
    },
    error: {
      general: null,
    },
    loading: {
      general: false,
      dashboard: false,
      dashboardRowTypes: false,
      dashboardWidgetTypes: false,
      programs: false,
      deletingRows: [],
      editingRow: false,
    },
    globalDefaults: {},
  };
}
export default {
  name: "ProgramDashboardManager",
  props: {
    isMobile: {
      type: Boolean,
      default: false,
    },
    client: {
      type: Object,
      required: false,
    },
  },
  components: {
    draggable,
    StatusChip,
    Button,
    Typography,
    BillboardCreator,
    TripleComponentCreator,
    DashboardCustomizeSideSheet,
    SummaryCreator,
    AddComponentPicker,
    ContentCreator,
    LeaderboardCreator,
    MetricsSummaryCreator,
    DashboardRowRenderer,
    ProgramSummaryCard,
    UnsavedChangesDialog,
    PreviewAudienceSelector,
  },
  data() {
    return initialState();
  },
  created() {
    this.loadClient();
  },
  methods: {
    // Canonical row key — delegates to shared util so admin preview and participant V2 can't drift.
    rowKey(row) {
      return rowKeyUtil(row);
    },
    compareRowIds(a, b) {
      return compareRowIdsUtil(a, b);
    },
    compareRows(a, b) {
      return compareNullableNumbers(a.position, b.position) || this.compareRowIds(a, b);
    },
    hasRenderableColumns(row) {
      return (row?.DashboardColumns ?? []).some(isRenderableDashboardColumn);
    },
    programDisplayName(programRow) {
      const rawTitle = programRow?.DashboardColumns?.[0]?.config?.title;

      // Current format: plain string
      if (typeof rawTitle === "string" && rawTitle.trim()) return rawTitle;

      // Legacy format: {"0":"T","1":"h","2":"i",…,"color":"#a8ffd7"}
      if (rawTitle && typeof rawTitle === "object") {
        const text = Object.keys(rawTitle)
          .filter((k) => !isNaN(k))
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => rawTitle[k])
          .join("");
        if (text.trim()) return text;
      }

      // Fallback: canonical program name from Program record
      return programRow?.Program?.displayName || "";
    },
    normalizeColumns(rows) {
      if (!rows) return rows;
      rows.forEach((row) => {
        if (row.DashboardColumns) {
          row.DashboardColumns.forEach((col) => {
            if (!col.columnId && col.dashboardColumnId) {
              col.columnId = col.dashboardColumnId;
            }
          });
        }
      });
      return rows;
    },
    resetPage() {
      Object.assign(this.$data, initialState());
    },
    handlePreviewClick() {
      this.previewSelectedGroups = [];
      this.showPreview = true;
    },
    handleCustomizePageClick() {
      this.dialog.customizePage = true;
    },
    handleCustomizeDrawerInput(isOpen) {
      if (!isOpen) {
        const sheet = this.$refs.customizeSideSheet;
        if (sheet && sheet.isDirty) {
          sheet.dialog.confirmCancel = true;
        } else {
          this.dialog.customizePage = false;
        }
      }
    },
    handleSummaryDrawerInput(isOpen) {
      if (!isOpen) {
        const sheet = this.$refs.summaryCreatorSheet;
        if (sheet && sheet.isDirty) {
          sheet.dialog.confirmCancel = true;
        } else {
          this.dialog.summaryEditor = false;
        }
      }
    },
    handleAddTopSectionComponent() {
      this.dialog.componentPickerContext = "topSection";
      this.dialog.componentPickerProgramRow = null;
      this.dialog.componentPicker = true;
    },
    loadClient() {
      this.resetPage();

      if (!this.client) {
        console.error("Client not found", this.globalClientId);
        return;
      }
      const { clientId } = this.client;

      this.clientId = clientId;

      this.getDashboardRowTypes();
      this.getDashboardWidgetTypes();
      this.getProgramDashboard();
      this.getPrograms();
      this.getProgramRows();
      this.getClientDashboardDefaults();
    },
    async getClientDashboardDefaults() {
      try {
        const res = await ProgramService.getClientDashboardDefaults(this.clientId);
        this.globalDefaults = res?.data?.result || {};
      } catch (e) {
        // Non-critical — creators will use their own hardcoded defaults
      }
    },
    async getDashboardRowTypes() {
      this.loading.dashboardRowTypes = true;
      try {
        const rows = (await ProgramService.getDashboardRowTypes())?.data?.result?.rows || [];
        this.rowTypes = rows;
      } catch (e) {
        console.error("Error fetching program dashboard row types ", e);
      } finally {
        this.loading.dashboardRowTypes = false;
      }
    },
    async getDashboardWidgetTypes() {
      this.loading.dashboardWidgetTypes = true;
      try {
        const rows =
          (
            await ProgramService.getDashboardWidgetTypes({
              params: {
                clientId: this.globalClientId,
              },
            })
          )?.data?.result?.rows || [];
        this.widgetTypes = rows;
      } catch (e) {
        console.error("Error fetching program dashboard widget types ", e);
      } finally {
        this.loading.dashboardWidgetTypes = false;
      }
    },
    async getProgramDashboard() {
      this.loading.dashboard = true;
      try {
        const rows =
          (
            await ProgramService.getProgramDashboardV2({
              preview: true,
              clientId: this.clientId,
            })
          )?.result?.rows || [];
        this.normalizeColumns(rows);
        this.rows = rows;
        this.topSectionRows = rows
          .filter((r) => !r.programId)
          .sort((a, b) => this.compareRows(a, b));
      } catch (e) {
        console.error("Error fetching program dashboard rows ", e);
      } finally {
        this.loading.dashboard = false;
      }
    },
    async getPrograms() {
      this.loading.programs = true;
      try {
        const programs =
          (
            await ProgramService.getProgramsV2({
              filter: `clientId = ${this.clientId}`,
              limit: 1000,
            })
          )?.result?.rows || [];
        this.programs = programs;
      } catch (e) {
        console.error("Error fetching programs ", e);
      } finally {
        this.loading.programs = false;
      }
    },
    async handleAddRowType(rowType) {
      this.dialog.componentEditorType = rowType.type;
      this.dialog.componentEditorRow = {
        rowTypeId: rowType.rowTypeId,
        programId: this.dialog.dashboardRowProgram,
      };
      this.dialog.componentEditor = true;
      this.dialog.dashboardRowType = false;
      this.dialog.addingToTopSection = false;
      this.dialog.dashboardRowProgram = null;
    },
    handleEditRow(row) {
      if (this.loading.deletingRows?.includes(row?.rowId)) return;

      const rowType = row.DashboardRowType?.type;

      // Metrics summary has its own dedicated editor
      if (rowType === "metrics-summary") {
        const column = row.DashboardColumns?.[0] || null;
        this.dialog.metricsSummaryEditorRowId = row.rowId;
        this.dialog.metricsSummaryEditorAudienceGroups = row.audienceGroups || null;
        const clonedColumn = column ? JSON.parse(JSON.stringify(column)) : null;
        if (clonedColumn && !clonedColumn.columnId && clonedColumn.dashboardColumnId) {
          clonedColumn.columnId = clonedColumn.dashboardColumnId;
        }
        this.dialog.metricsSummaryEditorColumn = clonedColumn;
        this.dialog.metricsSummaryEditorWidgetTypeId =
          column?.DashboardWidgetType?.widgetTypeId ||
          this.widgetTypes?.find((wt) => wt.type === "metrics-summary")?.widgetTypeId ||
          null;
        this.dialog.metricsSummaryEditor = true;
        return;
      }

      this.dialog.componentEditorType = rowType || (row.rowTypeId === 3 ? "triple" : null);
      this.dialog.componentEditorRow = JSON.parse(JSON.stringify(row));
      if (this.dialog.componentEditorRow.DashboardColumns) {
        this.dialog.componentEditorRow.DashboardColumns.forEach((col) => {
          if (!col.columnId && col.dashboardColumnId) col.columnId = col.dashboardColumnId;
        });
      }
      const ownerRow = this.programRows.find((pr) => pr.programId === row.programId);
      this.dialog.componentEditorRow.isLearning =
        ownerRow != null &&
        (ownerRow.Program?.type == null || ownerRow.Program?.type === "learning");
      this.dialog.componentEditorProgramName =
        row?.Program?.displayName || ownerRow?.Program?.displayName || null;
      this.dialog.componentEditor = true;
      this.dialog.dashboardRowType = false;
    },
    handleEditRowProgram(rowId, programId) {
      // Called to create a new row too - rowId and programId are null
      this.dialog.dashboardRowEditRowId = rowId;
      this.dialog.dashboardRowProgram = programId;
      this.dialog.dashboardRowType = true;
    },
    async handleUpdateRow() {
      try {
        this.loading.editingRow = true;
        await ProgramService.updateDashboardRow(this.dialog.dashboardRowEditRowId, {
          programId: this.dialog.dashboardRowProgram,
        });
        this.dialog.dashboardRowType = false;
        this.dialog.dashboardRowEditRowId = null;
        this.dialog.dashboardRowProgram = null;
        await this.getProgramDashboard();
      } catch (e) {
        console.error("Error updating row", e);
      } finally {
        this.loading.editingRow = false;
      }
    },
    async handleDeleteRow(rowId) {
      this.loading.deletingRows.push(rowId);
      try {
        await ProgramService.deleteDashboardRow(rowId);
        await this.getProgramDashboard();
      } catch (error) {
        console.error("Failed to delete row:", error);
      } finally {
        this.loading.deletingRows = this.loading.deletingRows.filter((id) => id !== rowId);
      }
    },
    async handleTopSectionReorder() {
      try {
        const rowIds = this.topSectionRows.map((r) => r.rowId);
        await ProgramService.updateDashboardRowOrder(rowIds, {});
      } catch (error) {
        console.error("Failed to reorder rows:", error);
      }
    },
    handleDeleteRowConfirm(rowId) {
      this.dialog.confirmDelete = true;
      this.dialog.confirmDeleteRowId = rowId;
    },
    async handleDeleteRowConfirmed() {
      const rowId = this.dialog.confirmDeleteRowId;
      if (!rowId) return;
      this.loading.deletingRows.push(rowId);
      try {
        await ProgramService.deleteDashboardRow(rowId);
        this.rows = this.rows.filter((row) => row.rowId !== rowId);
        this.topSectionRows = this.topSectionRows.filter((row) => row.rowId !== rowId);
        this.programRows = this.programRows.map((pr) => ({
          ...pr,
          triples: (pr.triples || []).filter((t) => t.rowId !== rowId),
        }));
        // Adjust page if current page is now empty
        if (this.programsCurrentPage > this.totalProgramPages) {
          this.programsCurrentPage = Math.max(1, this.totalProgramPages);
        }
      } catch (error) {
        console.error("Failed to delete row:", error);
      } finally {
        this.loading.deletingRows = this.loading.deletingRows.filter((id) => id !== rowId);
        this.dialog.confirmDelete = false;
        this.dialog.confirmDeleteRowId = null;
      }
    },
    async getProgramRows() {
      this.loadingProgramRows = true;
      try {
        const response = await ProgramService.getProgramDashboardRows({
          clientId: this.clientId,
        });
        const today = new Date(new Date().toISOString().split("T")[0]);
        const rawRows = response?.data?.result?.rows || [];
        this.normalizeColumns(rawRows);
        this.allProgramRows = rawRows
          .filter((row) => row.Program?.status !== "Incomplete")
          .sort((a, b) => this.compareRows(a, b))
          .map((r) => ({
            ...r,
            triples: (r.triples || []).sort(compareSubRows),
          }));
        const newRows = rawRows
          .filter((row) => {
            const status = row.Program?.status;
            if (status === "Incomplete" || status === "Archived") return false;
            const endDate = row.Program?.endDate;
            if (endDate && new Date(endDate) < today) return false;
            return true;
          })
          .sort((a, b) => this.compareRows(a, b))
          .map((r) => ({
            ...r,
            triples: (r.triples || []).sort(compareSubRows),
          }));
        if (newRows.length > 0 || this.programRows.length === 0) {
          this.programRows = newRows;
        }
      } catch (e) {
        console.error("Error fetching program rows", e);
        this.programRows = [];
      } finally {
        this.loadingProgramRows = false;
      }
    },
    handleEditProgramSummary(programRow) {
      this.dialog.summaryEditorRow = JSON.parse(JSON.stringify(programRow));
      this.dialog.summaryEditor = true;
    },
    handleProgramPageInput(newPageOrder) {
      const start = (this.programsCurrentPage - 1) * this.programsItemsPerPage;
      const currentPageLength = this.paginatedProgramRows.length;
      this.programRows.splice(start, currentPageLength, ...newPageOrder);
    },
    async handleProgramCardReorder() {
      try {
        const rowIds = this.programRows.map((r) => r.rowId);
        await ProgramService.updateDashboardRowOrder(rowIds, {});
      } catch (error) {
        console.error("Failed to reorder program cards:", error);
      }
    },
    async handleTripleReorder(programRow) {
      try {
        const rowIds = (programRow.triples || []).map((r) => r.rowId);
        await ProgramService.updateDashboardRowOrder(rowIds, { params: { target: "subPosition" } });
      } catch (error) {
        console.error("Failed to reorder triples:", error);
      }
    },
    async handleSubcomponentRefresh() {
      this.dialog.openedFromPicker = false;
      const scrollContainer = this.getScrollContainer();
      const scrollTop = scrollContainer.scrollTop;
      await Promise.all([this.getProgramDashboard(), this.getProgramRows()]);
      this.$nextTick(() => {
        scrollContainer.scrollTop = scrollTop;
      });
    },
    async handleMetricsSummarySaved() {
      this.dialog.openedFromPicker = false;
      this.dialog.metricsSummaryEditor = false;
      await this.handleSubcomponentRefresh();
    },
    handleAddSubcomponent(programRow) {
      this.dialog.componentPickerContext = "programList";
      this.dialog.componentPickerProgramRow = programRow;
      this.dialog.componentPicker = true;
    },
    async handleComponentPickerSelect(option) {
      const programRow = this.dialog.componentPickerProgramRow;
      const isLearning =
        programRow != null &&
        (programRow.Program?.type == null || programRow.Program?.type === "learning");

      if (option.type === "metrics-summary") {
        const rowType = this.rowTypes.find((rt) => rt.type === "metrics-summary");
        if (!rowType) {
          this.$store.dispatch("showSnackbar", {
            text: "Component type not available. Please try again.",
            color: "error",
          });
          return;
        }

        try {
          const createdRow = await ProgramService.createDashboardRow({
            rowTypeId: rowType.rowTypeId,
            programId: null,
            clientId: this.clientId,
            position: this.topSectionRows.length + 1,
          });
          const rowId = createdRow?.data?.result?.rowId;
          if (!rowId) throw new Error("Failed to create row");

          this.dialog.metricsSummaryEditorRowId = rowId;
          this.dialog.metricsSummaryEditorColumn = null;
          this.dialog.metricsSummaryEditorAudienceGroups = null;
          this.dialog.metricsSummaryEditorWidgetTypeId =
            this.widgetTypes?.find((wt) => wt.type === "metrics-summary")?.widgetTypeId || null;
          this.dialog.openedFromPicker = true;
          this.dialog.componentPicker = false;
          this.dialog.metricsSummaryEditor = true;
        } catch (error) {
          console.error("Failed to create metrics summary row:", error);
          this.$store.dispatch("showSnackbar", {
            text: "Unable to open Metrics Summary. Please try again.",
            color: "error",
          });
        }
        return;
      }

      // Find the matching row type from the loaded rowTypes
      const rowType = this.rowTypes.find((rt) => rt.type === option.type);
      if (!rowType) {
        this.$store.dispatch("showSnackbar", {
          text: "Component type not available. Please try again.",
          color: "error",
        });
        return;
      }

      this.dialog.componentEditorType = option.type;
      this.dialog.componentEditorRow = {
        rowTypeId: rowType?.rowTypeId ?? null,
        programId: programRow?.programId || null,
        isSubcomponent: !!programRow,
        isLearning: !!programRow && isLearning,
      };
      this.dialog.componentEditorProgramName = programRow?.Program?.displayName || null;
      this.dialog.openedFromPicker = true;
      this.dialog.componentPicker = false;
      this.dialog.componentEditor = true;
    },
    handleComponentPickerClose() {
      this.dialog.componentPicker = false;
      this.dialog.componentPickerContext = null;
      this.dialog.componentPickerProgramRow = null;
    },
    handleComponentEditorClose() {
      this.dialog.componentEditor = false;
      if (this.dialog.openedFromPicker) {
        this.dialog.openedFromPicker = false;
        this.dialog.componentPicker = true;
      }
    },
    handleMetricsSummaryEditorClose() {
      this.dialog.metricsSummaryEditor = false;
      if (this.dialog.openedFromPicker) {
        this.dialog.openedFromPicker = false;
        this.dialog.componentPicker = true;
      }
    },
    handleOpenSubSidesheet({ slotIndex, column, mode }) {
      this.dialog.subSidesheetSlot = slotIndex;
      this.dialog.subSidesheetColumn = column;
      this.dialog.subSidesheetMode = mode;
      this.dialog.subSidesheetActiveType =
        column?.DashboardWidgetType?.type === "leaderboard" ? "leaderboard" : "content";
      this.dialog.subSidesheetDrawer = true;
    },
    handleTripleComponentSaved() {
      this.dialog.openedFromPicker = false;
      this.handleSubcomponentRefresh();
    },
    async handleSubSidesheetSaved() {
      this.dialog.subSidesheetDrawer = false;
      const scrollContainer = this.getScrollContainer();
      const scrollTop = scrollContainer.scrollTop;
      await this.getProgramRows();
      const freshRow = this.programRows.find(
        (r) => r.rowId === this.dialog.componentEditorRow?.rowId
      );
      if (freshRow) {
        this.dialog.componentEditorRow = { ...freshRow };
      }
      this.$nextTick(() => {
        scrollContainer.scrollTop = scrollTop;
      });
    },
    buildPreviewProgram(programRow) {
      const p = programRow.Program || {};
      const status = p.status;
      const programState =
        status === "Active"
          ? "active"
          : status === "Inactive"
          ? "inactive"
          : status === "Completed"
          ? "completed"
          : status === "Archived"
          ? "inactive"
          : "not_started";
      return {
        programId: p.programId,
        displayName: p.displayName || p.name,
        name: p.displayName || p.name,
        status: p.status,
        programState,
        startDate: p.startDate,
        endDate: p.endDate,
        imageURL: p.imageURL || p.imageUrl,
        imageUrl: p.imageURL || p.imageUrl,
        type: p.type,
        progressPercent: 0,
        earnings: null,
      };
    },
    getScrollContainer() {
      let el = this.$el;
      while (el && el !== document.documentElement) {
        const { overflowY } = window.getComputedStyle(el);
        if (overflowY === "auto" || overflowY === "scroll") return el;
        el = el.parentElement;
      }
      return document.documentElement;
    },
    formatProgramDate(dateStr) {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-US", {
        month: "numeric",
        day: "numeric",
        year: "2-digit",
      });
    },
    toggleProgramExpand(rowId) {
      this.$set(this.expandedPrograms, rowId, !this.expandedPrograms[rowId]);
    },
    topSectionRowTitle(row) {
      const type = row?.DashboardRowType?.type;
      if (type === "billboard" || type === "headline") {
        const config = row?.DashboardColumns?.[0]?.config || {};
        const aspectRatio = config?.aspectRatio;
        const sizeLabel = aspectRatio === "4:1" ? "Large" : "Medium";
        return `Billboard, ${sizeLabel}`;
      }
      if (type === "triple") return "Triple Component";
      if (type === "metrics-summary") return "Metric Summary";
      return row?.DashboardRowType?.name || "Component";
    },
    topSectionRowLabel(row) {
      const type = row?.DashboardRowType?.type;
      if (type === "billboard" || type === "headline") {
        const config = row?.DashboardColumns?.[0]?.config || {};
        // Legacy headline widgets saved config.title instead of config.overlay;
        // fall back so pre-existing rows don't show a blank label.
        return config?.overlay?.text || config?.title?.text || "";
      }
      return "";
    },
    audienceLabelText(row) {
      if (!row.audienceGroups || row.audienceGroups.length === 0) {
        return "Everyone";
      }
      const names = row.audienceGroups.map((g) => g.groupDisplayName);
      if (names.length <= 2) return names.join(", ");
      return `${names[0]}, ${names[1]} +${names.length - 2} more`;
    },
    audienceLabelTooltip(row) {
      if (!row.audienceGroups || row.audienceGroups.length <= 2) return null;
      return row.audienceGroups.map((g) => g.groupDisplayName).join(", ");
    },
  },
  computed: {
    ...mapState(["userProfile", "clients", "globalClientId"]),
    paginatedProgramRows() {
      const start = (this.programsCurrentPage - 1) * this.programsItemsPerPage;
      return this.programRows.slice(start, start + this.programsItemsPerPage);
    },
    totalProgramPages() {
      return Math.ceil(this.programRows.length / this.programsItemsPerPage) || 1;
    },
    programsPerPageOptions() {
      return [10, 20, 50];
    },
    maxRowPosition() {
      if (!this.rows || this.rows.length === 0) return 0;
      return Math.max(...this.rows.map((r) => r.position || 0));
    },
    previewSelectedGroupIds() {
      // Empty selection = All Content (null → show everything unfiltered).
      return this.previewSelectedGroups && this.previewSelectedGroups.length > 0
        ? this.previewSelectedGroups.map((g) => g.groupId)
        : null;
    },
    previewTopSectionRows() {
      return this.topSectionRows.filter(
        (row) =>
          this.hasRenderableColumns(row) &&
          isRowVisibleForPreview(row, this.previewSelectedGroupIds)
      );
    },
    previewProgramRows() {
      return this.allProgramRows
        .filter((row) => isProgramVisibleForPreview(row, this.previewSelectedGroupIds))
        .filter((row) => {
          // parity: participant V2 hides Archived/Scheduled + PROCORE programs
          const status = row.Program?.status;
          const type = (row.Program?.type || "").toUpperCase();
          return status !== "Archived" && status !== "Scheduled" && type !== "PROCORE";
        })
        .map((row) => ({
          ...row,
          triples: (row.triples || []).filter((triple) => this.hasRenderableColumns(triple)),
        }));
    },
    previewCaptionText() {
      if (!this.previewSelectedGroups || this.previewSelectedGroups.length === 0) {
        return "Showing everything on this dashboard. Most participants belong to specific groups (or none) and won't see this much.";
      }
      const names = this.previewSelectedGroups.map((g) => g.groupDisplayName);
      return `Simulating a participant in: ${names.join(", ")}.`;
    },
  },
  watch: {
    client: {
      handler() {
        this.loadClient();
      },
    },
  },
};
</script>

<style scoped>
.row-type-container {
  border: 1px solid var(--v-brandPrimary-base);
  padding: 10px;
  border-radius: 8px;
  /* font-weight: bold; */
  cursor: pointer;
}
.row-type-container:hover {
  background-color: #eeeeee;
}

.row-type-background {
  background-color: lightgrey;
  padding: 16px 4px 16px 4px;
  width: 100%;
  /* border-radius: 4px; */
}

#add-row-container {
  text-align: center;
  padding: 8px 0 8px 0;
  border: 1px solid var(--v-brandPrimary-base);
  font-weight: bold;
  color: var(--v-brandPrimary-base);
  cursor: pointer;
}

#add-row-container:hover {
  background-color: #eeeeee;
}

#row-container {
  /* border: 1px solid var(--v-brandCyan-base); */
  padding: 8px;
}

.row-item {
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 8px 12px;
  background-color: #ffffff;
  min-height: 60px;
}

.empty-state-text {
  color: rgba(0, 0, 0, 0.4);
  font-size: 0.875rem;
}

.program-card {
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background-color: #ffffff;
}

.program-card-handle {
  cursor: grab;
}

.triple-handle {
  cursor: grab;
}

.triple-card {
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  background-color: #ffffff;
}

.pagination-controls {
  gap: 41px;
}

.pagination-label {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-size: 12px;
  line-height: 21px;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

.pagination-per-page-select {
  flex: 0 0 auto;
  max-width: 56px;
}

.pagination-per-page-select >>> .v-select__selection,
.pagination-per-page-select >>> input {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.87);
}

.row-title {
  width: 20%;
  min-width: 20%;
  flex-shrink: 0;
}

.row-label {
  flex: 1;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row-audience {
  flex-shrink: 0;
  margin-left: 16px;
}

.audience-display {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: default;
}

.audience-icon {
  color: rgba(0, 0, 0, 0.6) !important;
  flex-shrink: 0;
}

.audience-text {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-weight: 350;
  font-size: 14px;
  line-height: 22px;
  color: rgba(0, 0, 0, 0.87);
  width: 188px;
  max-width: 188px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.row-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
  flex-shrink: 0;
}
.no-wrap {
  white-space: nowrap;
}

/* ── Preview Dialog ─────────────────────────────────────────────────────────── */
.preview-dialog-card {
  background: #eeeeee !important;
}

.preview-banner {
  background: #ffc107;
  padding: 12px 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
}

.preview-banner__icon {
  width: 24px;
  height: 24px;
  color: rgba(0, 0, 0, 0.87) !important;
}

.preview-banner__title {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: rgba(0, 0, 0, 0.87);
}

.preview-banner__label {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

.preview-banner__selector {
  flex: 0 0 auto;
}
.preview-caption-row {
  background: #e5f6fd;
}
.preview-caption-inner {
  padding: 6px 24px;
  color: #014361;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-family: "Avenir", Helvetica, Arial, sans-serif;
  font-size: 14px;
  line-height: 22px;
}
.preview-caption-icon {
  color: #1876d2 !important;
  margin-top: 1px;
}

.preview-close-btn {
  width: 40px;
  height: 40px;
  color: rgba(0, 0, 0, 0.87) !important;
}

.preview-content {
  background: #eeeeee;
  padding: 24px 0 0 !important;
}

.preview-your-programs-section {
  background: #fff;
  border: 1px solid rgba(36, 55, 62, 0.12);
  border-radius: 8px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);
  margin-top: 24px;
  overflow: hidden;
}

.preview-your-programs-toolbar {
  display: flex;
  align-items: center;
  padding: 16px 32px;
  gap: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.preview-toolbar-header {
  display: flex;
  align-items: center;
  gap: 16px;
}

.preview-toolbar-icon-box {
  background: #002a4e;
  border-radius: 4px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-toolbar-title {
  font-family: "Avenir", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: #000000;
}

.preview-toolbar-divider {
  width: 1px;
  height: 46px;
  background: #d9d9d9;
  margin: 0 8px;
  flex-shrink: 0;
}

.preview-pill-btn {
  border: none;
  background: none;
  border-radius: 18px;
  padding: 8px 16px;
  color: #077daf;
  font-family: "Avenir", sans-serif;
  font-size: 14px;
  font-weight: 350;
  letter-spacing: 1.25px;
  text-transform: uppercase;
  cursor: default;
  opacity: 0.6;
}

.preview-toolbar-search {
  max-width: 463px;
  width: 463px;
}

.preview-program-group {
  padding: 24px 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.preview-program-group:first-child {
  border-top: none;
}

@media (max-width: 959px) {
  .preview-content >>> .program-card__content {
    width: 100%;
  }
}
</style>
